<template>
  <Drawer
    v-if="hostReady"
    :key="drawerKey"
    :visible="dialogVisible"
    title="变更记录"
    placement="right"
    width="96%"
    :mask="true"
    :mask-closable="false"
    :closable="true"
    :destroy-on-close="true"
    :z-index="110"
    :get-container="resolveContainer"
    wrapper-class-name="change-record-drawer-portal"
    :wrap-class-name="wrapClassName"
    class="change-record-drawer xm-drawer"
    :style="{ position: 'absolute' }"
    :drawer-style="{ position: 'absolute' }"
    :body-style="{ padding: 0, height: '100%', overflow: 'hidden' }"
    @close="close"
  >
    <div class="change-record-drawer__body" :key="contentKey">
      <div class="change-record-drawer__left" @scroll="onLeftScroll">
        <a-spin :spinning="listLoading">
          <div v-if="listErrorMsg" class="change-record-drawer__state">
            <a-result status="error" :title="listErrorMsg">
              <template #extra>
                <a-button type="primary" @click="retryList">重试</a-button>
              </template>
            </a-result>
          </div>
          <div v-else-if="!listLoading && records.length === 0" class="change-record-drawer__state">
            <a-empty description="暂无变更记录" />
          </div>
          <div v-else class="change-record-drawer__list">
            <div
              v-for="item in records"
              :key="item.id"
              class="change-record-drawer__item"
              :class="{ 'is-active': selectedId === item.id }"
              @click="selectVersion(item.id)"
            >
              <div class="change-record-drawer__item-head">
                <span class="change-record-drawer__item-ver">V{{ item.versionNo }}</span>
                <a-tag
                  v-if="operateTypeLabel(item.operateType)"
                  :color="operateTypeColor(item.operateType)"
                >
                  {{ operateTypeLabel(item.operateType) }}
                </a-tag>
              </div>
              <div class="change-record-drawer__item-meta">{{ item.operateTime || '-' }}</div>
              <div class="change-record-drawer__item-meta">{{ item.operatorName || '-' }}</div>
            </div>
            <div v-if="loadingMore" class="change-record-drawer__more">加载中...</div>
            <div v-else-if="loadMoreErrorMsg" class="change-record-drawer__more is-error">
              <span>{{ loadMoreErrorMsg }}</span>
              <a-button type="link" size="small" @click="retryLoadMore">重试</a-button>
            </div>
          </div>
        </a-spin>
      </div>
      <div class="change-record-drawer__right">
        <div v-if="!selectedId" class="change-record-drawer__state">
          <a-empty description="请选择左侧版本查看详情" />
        </div>
        <ChangeRecordDetailPanel
          v-else
          :key="`${selectedId}:${detailPageCode}:${detailModuleCode}`"
          :version-id="selectedId"
          :page-code="detailPageCode"
          :module-code="detailModuleCode"
        />
      </div>
    </div>
  </Drawer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
export default defineComponent({ name: 'ChangeRecordDrawer' });
</script>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  Drawer,
  Empty as AEmpty,
  Spin as ASpin,
  Result as AResult,
  Button as AButton,
  Tag as ATag,
} from 'ant-design-vue';
import emitter from '@/events/eventBus';
import GoingUtils from '@/utils/goingUtils';
import {
  operateTypeColor,
  operateTypeLabel,
  useChangeRecordList,
} from '@/main-page/components/changeRecord/useChangeRecordList';
import ChangeRecordDetailPanel from '@/main-page/components/changeRecord/ChangeRecordDetailPanel.vue';

type OpenPayload = {
  tableCode?: string;
  bizId?: string;
};

type Session = {
  tableCode: string;
  bizId: string;
  fullPath: string;
};

const route = useRoute();
const session = ref<Session | null>(null);
const hostReady = ref(false);
const drawerKey = ref(0);
let boundContainer: HTMLElement | null = null;

const {
  loading: listLoading,
  loadingMore,
  errorMsg: listErrorMsg,
  loadMoreErrorMsg,
  records,
  selectedId,
  detailComponent,
  load: loadList,
  loadMore,
  retry: retryList,
  retryLoadMore,
  reset: resetList,
  select: selectVersion,
} = useChangeRecordList();

const detailPageCode = computed(() =>
  String(detailComponent.value?.componentPageCode ?? '').trim(),
);
const detailModuleCode = computed(() =>
  String(detailComponent.value?.componentModuleCode ?? '').trim(),
);

/** 显隐只跟本页会话走：切页签不关 visible，抽屉随 #page 一起被 KeepAlive 摘走/还原，切回不再滑入。 */
const dialogVisible = computed(() => !!session.value);

const contentKey = computed(() => {
  const cur = session.value;
  if (!cur) return 'empty';
  return `${cur.tableCode}:${cur.bizId}`;
});

const wrapClassName = computed(() => {
  const parts = ['change-record-drawer-wrap'];
  parts.push(dialogVisible.value ? 'is-active' : 'is-parked');
  return parts.join(' ');
});

const resolveContainer = () => {
  const pageEl = document.getElementById('page');
  if (pageEl) {
    boundContainer = pageEl;
    return pageEl;
  }
  const layoutEl = document.getElementById('PageLayout');
  if (layoutEl) {
    boundContainer = layoutEl;
    return layoutEl;
  }
  return false;
};

const containerNeedsRebind = () => {
  if (!hostReady.value) return true;
  if (boundContainer && !boundContainer.isConnected) return true;
  const pageEl = document.getElementById('page');
  return !!(pageEl && boundContainer && pageEl !== boundContainer);
};

const detachHost = () => {
  hostReady.value = false;
  boundContainer = null;
};

const applySession = (tableCode: string, bizId: string, fullPath: string) => {
  session.value = { tableCode, bizId, fullPath };
};

const open = (payload: OpenPayload) => {
  const tableCode = String(payload?.tableCode ?? '').trim();
  const bizId = String(payload?.bizId ?? '').trim();
  const fullPath = String(route.fullPath ?? '').trim();
  if (!tableCode || !bizId) {
    GoingUtils.showWarningMsg('请传入领域对象编码和业务主键');
    return;
  }
  if (!fullPath) {
    GoingUtils.showWarningMsg('当前页无法打开变更记录');
    return;
  }
  if (containerNeedsRebind()) {
    detachHost();
    drawerKey.value += 1;
    applySession(tableCode, bizId, fullPath);
    nextTick(() => {
      hostReady.value = true;
    });
    return;
  }
  hostReady.value = true;
  applySession(tableCode, bizId, fullPath);
};

const close = () => {
  session.value = null;
  resetList();
};

const closeByFullPath = (fullPath: string) => {
  const path = String(fullPath ?? '').trim();
  if (path && session.value?.fullPath === path) {
    close();
    detachHost();
    return;
  }
  if (boundContainer && !boundContainer.isConnected) {
    detachHost();
  }
};

const onLeftScroll = (e: Event) => {
  const el = e.target as HTMLElement | null;
  if (!el) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
    loadMore();
  }
};

watch(
  () => session.value,
  (cur) => {
    if (!cur) {
      resetList();
      return;
    }
    loadList(cur.tableCode, cur.bizId);
  },
);

onMounted(() => {
  emitter.on('openChangeRecord', open);
  emitter.on('closeChangeRecord', close);
  emitter.on('closePageContentDrawerByPath', closeByFullPath);
});

onBeforeUnmount(() => {
  emitter.off('openChangeRecord', open);
  emitter.off('closeChangeRecord', close);
  emitter.off('closePageContentDrawerByPath', closeByFullPath);
});
</script>

<style lang="less">
/*
 * 抽屉 DOM 挂在 #page，随页签 KeepAlive 一起摘走/还原，切回不再播放打开动画。
 * Ant Design Vue 会在 getContainer 内额外创建 portal 容器；关闭后该容器仍在。
 * 未打开的 wrap 不能再 inset:0 挡交互，否则关掉后再点会像没反应。
 */
#page:has(> .change-record-drawer-portal) {
  position: relative;
}

#page > .change-record-drawer-portal,
#PageLayout > .change-record-drawer-portal {
  position: absolute !important;
  inset: 0 !important;
  width: auto !important;
  height: auto !important;
  z-index: 110 !important;
  pointer-events: none;
}

#page .change-record-drawer-wrap.ant-drawer-open,
#PageLayout .change-record-drawer-wrap.ant-drawer-open {
  position: absolute !important;
  inset: 0;
  z-index: 110;
  pointer-events: auto;
}

#page .change-record-drawer-wrap:not(.ant-drawer-open),
#page .change-record-drawer-wrap.is-parked,
#PageLayout .change-record-drawer-wrap:not(.ant-drawer-open),
#PageLayout .change-record-drawer-wrap.is-parked {
  position: absolute !important;
  inset: auto !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  pointer-events: none !important;
  z-index: -1 !important;
}

#page .change-record-drawer-wrap .ant-drawer-mask,
#page .change-record-drawer-wrap .ant-drawer-content-wrapper,
#PageLayout .change-record-drawer-wrap .ant-drawer-mask,
#PageLayout .change-record-drawer-wrap .ant-drawer-content-wrapper {
  position: absolute !important;
}

#page .change-record-drawer-wrap .ant-drawer-content-wrapper,
#PageLayout .change-record-drawer-wrap .ant-drawer-content-wrapper {
  width: 96% !important;
}

.change-record-drawer {
  .ant-drawer-body {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  &__body {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__left {
    width: 200px;
    flex-shrink: 0;
    border-right: 1px solid #f0f0f0;
    overflow: auto;
  }

  &__list {
    min-height: 240px;
  }

  &__item {
    padding: 12px;
    cursor: pointer;
    border-bottom: 1px solid #f5f5f5;

    &:hover {
      background: #f7f8fa;
    }

    &.is-active {
      background: #e8f3ff;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;

    .ant-tag {
      margin-right: 0;
    }
  }

  &__item-ver {
    font-weight: 600;
    color: #1d2129;
    line-height: 22px;
  }

  &__item-meta {
    font-size: 12px;
    color: #86909c;
    line-height: 20px;
  }

  &__more {
    padding: 12px 0;
    text-align: center;
    font-size: 12px;
    color: #86909c;

    &.is-error {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      color: #f53f3f;
      padding: 8px 12px 16px;
    }
  }

  &__right {
    flex: 1;
    min-width: 0;
    overflow: auto;
  }

  &__state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    padding: 24px 12px;
  }
}
</style>
